<template>
  <div class="datasets-overlay" @click.self="close">
    <section class="datasets-modal" role="dialog" aria-modal="true" aria-label="Experiment datasets">
      <header class="datasets-header">
        <h2><i class="bi bi-table"></i> Experiment datasets</h2>
        <button type="button" :disabled="busy || actionBusy" aria-label="Close datasets" @click="close"><i class="bi bi-x-lg"></i></button>
      </header>
      <nav class="dataset-tabs" aria-label="Dataset tools">
        <button :class="{ active: tab === 'browse' }" @click="tab = 'browse'">Browse</button>
        <button :class="{ active: tab === 'import' }" @click="tab = 'import'">Import CSV</button>
      </nav>
      <div class="datasets-body">
        <TabularDatasetImport v-show="tab === 'import'" :experiment-id="experimentId" :entra-id="entraId"
          @busyChange="busy = $event" @unresolvedAttemptChange="unresolved = $event"
          @queued="queued" @fileCommitted="$emit('fileCommitted', $event)" />
        <section v-show="tab === 'browse'">
          <div class="dataset-picker">
            <label>Dataset
              <select v-model="selectedId" :disabled="loading || actionBusy">
                <option value="">Select a dataset</option>
                <option v-for="dataset in datasets" :key="dataset.id" :value="dataset.id">{{ dataset.title }} — {{ dataset.status }}</option>
              </select>
            </label>
            <button type="button" :disabled="loading || actionBusy" @click="load">{{ loading ? 'Loading…' : 'Refresh datasets' }}</button>
          </div>
          <p v-if="!loading && !datasets.length && !error">No tabular datasets yet. Use Import CSV to add one.</p>
          <p v-if="capped" class="notice">The server returned its 200-dataset limit. Older datasets may not appear in this list.</p>
          <pre v-if="error" class="error" role="alert">{{ error }}</pre>
          <section v-if="selected" class="status-card" aria-live="polite">
            <h3>{{ selected.title }} <span class="badge">{{ selected.status }}</span></h3>
            <p v-if="['queued', 'processing'].includes(selected.status)">Import is queued or processing. Full-file validation is performed by the worker.</p>
            <p v-if="selected.row_count != null">Rows: {{ selected.row_count }}</p>
            <p v-if="selected.job">Job: {{ selected.job.status }}<span v-if="selected.job.progress_rows != null"> · {{ selected.job.progress_rows }} rows processed</span><span v-if="selected.job.attempts != null"> · attempt {{ selected.job.attempts }}</span></p>
            <pre v-if="selected.error || selected.job?.error" class="error">{{ selected.error || selected.job.error }}</pre>
            <p v-if="selected.status === 'cancelled'">Import cancelled. No dataset rows are available; the original uploaded source file is retained.</p>
            <p v-else-if="selected.job?.status === 'cancelled'">The {{ selected.job.type || 'latest' }} job was cancelled.</p>
            <p v-if="selected.status === 'deleting'" class="notice">Dataset unavailable. Cleanup removes Parquet and export artifacts; the original uploaded source file is retained.</p>
            <p v-if="selected.status === 'deleted'">Dataset artifacts have been deleted. The original uploaded file was retained.</p>
            <div class="dataset-actions">
              <button v-if="canCancelImport" type="button" :disabled="busy || actionBusy" @click="cancelImport">{{ actionBusy ? 'Working…' : 'Cancel import' }}</button>
              <button v-if="selected.status !== 'deleted'" type="button" class="danger-action" :disabled="busy || actionBusy || (selected.status === 'deleting' && !deletionFailed)" @click="deleteDataset">
                <i class="bi bi-trash" aria-hidden="true"></i> {{ deletionFailed ? 'Retry dataset cleanup' : selected.status === 'deleting' ? 'Deleting dataset…' : 'Delete dataset' }}
              </button>
            </div>
            <pre v-if="actionError" class="error" role="alert">{{ actionError }}</pre>
            <pre v-if="pollError" class="error" role="alert">{{ pollError }}</pre>
            <button v-if="pollError" type="button" @click="restartPolling">Retry status</button>
          </section>
          <TabularDatasetBrowser v-if="selected && selected.status === 'ready' && !mutationPending" :key="selected.id" :dataset="selected" :entra-id="entraId" />
        </section>
      </div>
    </section>
  </div>
</template>

<script>
import apiService from '@/services/apiService';
import TabularDatasetImport from './TabularDatasetImport.vue';
import TabularDatasetBrowser from './TabularDatasetBrowser.vue';
import { rowsErrorMessage } from '@/utils/tabularRows';
import { confirmDelete } from '@/utils/confirmDelete';

export default {
  name: 'TabularDatasetsPanel',
  components: { TabularDatasetImport, TabularDatasetBrowser },
  props: {
    experimentId: { type: [Number, String], required: true },
    entraId: { type: String, required: true },
  },
  emits: ['close', 'fileCommitted'],
  data() {
    return { tab: 'browse', datasets: [], selectedId: '', loading: false, error: '',
      busy: false, unresolved: false, capped: false, pollError: '', pollDelay: 2000,
      actionBusy: false, mutationPending: false, actionError: '' };
  },
  computed: {
    selected() { return this.datasets.find(item => item.id === this.selectedId) || null; },
    contextKey() { return JSON.stringify([this.experimentId, this.entraId]); },
    canCancelImport() {
      return ['queued', 'processing'].includes(this.selected?.status) &&
        this.selected?.job?.type !== 'delete' && this.selected?.job?.type !== 'export' &&
        this.selected?.job?.id != null && ['queued', 'running'].includes(this.selected.job.status);
    },
    deletionFailed() { return this.selected?.status === 'deleting' && (this.selected?.job?.status === 'failed' || !!this.actionError || !!this.pollError); },
  },
  watch: {
    selectedId() { this.actionError = ''; this.restartPolling(); },
    contextKey() { this.reset(); },
  },
  created() {
    this.disposed = false;
    this.pollGeneration = 0;
    this.listGeneration = 0;
    this.reset();
  },
  beforeUnmount() {
    this.disposed = true;
    this.listGeneration += 1;
    this.listController?.abort();
    this.actionController?.abort();
    this.stopPolling();
  },
  methods: {
    cancelImport() {
      if (!this.canCancelImport) return;
      return this.performAction('cancel');
    },
    deleteDataset() { return this.performAction('delete'); },
    async performAction(action) {
      const dataset = this.selected;
      if (!dataset || this.actionBusy || this.busy || !this.entraId?.trim() || dataset.status === 'deleted') return;
      const context = this.contextKey;
      const controller = new AbortController();
      this.actionController = controller;
      this.actionBusy = true;
      const current = () => !this.disposed && !controller.signal.aborted && context === this.contextKey && this.selectedId === dataset.id;
      const message = action === 'cancel'
        ? `Cancel the import for “${dataset.title}”? It cannot publish new output. The worker stops at its next checkpoint. The uploaded source file is retained.`
        : `Delete dataset “${dataset.title}”? It will immediately become unavailable, active imports/exports will be cancelled, and all Parquet/export artifacts will be removed. The original uploaded file is retained. This cannot be undone.`;
      try {
        if (!await confirmDelete(message) || !current()) return;
        this.mutationPending = true;
        this.actionError = '';
        this.stopPolling();
        // A pre-action list response must not restore stale ready/active state.
        this.listController?.abort();
        ++this.listGeneration;
        this.loading = false;
        if (action === 'cancel') {
          const response = await apiService.cancelTabularJob(dataset.job.id, this.entraId, controller.signal);
          if (!current()) return;
          if (!response?.job || String(response.job.id) !== String(dataset.job.id)) throw new Error('Cancel response is missing the requested job. Refresh status before retrying.');
          this.datasets = this.datasets.map(item => item.id === dataset.id ? {
            ...item, job: response.job,
            ...(response.job.status === 'cancelled' ? { status: 'cancelled' } : {}),
          } : item);
        } else {
          const response = await apiService.deleteTabularDataset(dataset.id, this.entraId, controller.signal);
          if (!current()) return;
          if (![200, 202].includes(response.status)) throw new Error('Unexpected dataset deletion response. Refresh status before retrying.');
          this.knownIds.add(dataset.id);
          this.datasets = this.datasets.map(item => item.id === dataset.id ? {
            ...item, status: response.status === 200 ? 'deleted' : 'deleting', job: null, error: null,
          } : item);
        }
      } catch (error) {
        if (current()) this.actionError = rowsErrorMessage(error);
      } finally {
        if (current()) {
          this.actionBusy = false;
          this.mutationPending = false;
          // Reconcile completion/cancellation races and ambiguous network errors.
          if (this.selected?.status !== 'deleted') this.restartPolling();
        }
      }
    },
    close() {
      if (this.busy || this.actionBusy) return;
      if (this.unresolved && !window.confirm('A confirmation has an unresolved outcome. Closing discards its retry key; the server may already have queued a dataset. Close anyway?')) return;
      this.$emit('close');
    },
    reset() {
      this.stopPolling();
      this.actionController?.abort();
      this.actionBusy = false;
      this.mutationPending = false;
      this.actionError = '';
      this.knownIds = new Set();
      this.datasets = [];
      this.selectedId = '';
      this.load();
    },
    async load() {
      if (this.actionBusy) return;
      this.listController?.abort();
      const controller = new AbortController();
      this.listController = controller;
      const generation = ++this.listGeneration;
      if (!this.entraId?.trim() || !this.experimentId) return;
      this.loading = true;
      this.error = '';
      const current = () => !this.disposed && !controller.signal.aborted && generation === this.listGeneration;
      try {
        const response = await apiService.listExperimentDatasets(this.experimentId, this.entraId, controller.signal);
        if (!current()) return;
        if (!Array.isArray(response?.datasets)) throw new Error('Dataset list response is missing datasets.');
        const retained = this.datasets.filter(item => this.knownIds?.has(item.id) && !response.datasets.some(row => row.id === item.id));
        this.datasets = [...retained, ...response.datasets];
        this.capped = response.datasets.length >= 200;
        if (!this.selected && this.datasets.length) this.selectedId = this.datasets[0].id;
        else this.restartPolling();
      } catch (error) {
        if (current()) this.error = rowsErrorMessage(error);
      } finally { if (current()) this.loading = false; }
    },
    queued(dataset) {
      this.knownIds = this.knownIds || new Set();
      this.knownIds.add(dataset.id);
      this.datasets = [dataset, ...this.datasets.filter(item => item.id !== dataset.id)];
      this.selectedId = dataset.id;
      this.tab = 'browse';
      this.restartPolling();
    },
    stopPolling() {
      this.pollGeneration += 1;
      clearTimeout(this.pollTimer);
      this.pollController?.abort();
    },
    restartPolling() {
      this.stopPolling();
      this.pollError = '';
      this.pollDelay = 2000;
      if (this.selected && !this.actionBusy) this.poll(this.pollGeneration);
    },
    async poll(generation) {
      const dataset = this.selected;
      if (!dataset || this.disposed || generation !== this.pollGeneration) return;
      const controller = new AbortController();
      this.pollController = controller;
      const current = () => !this.disposed && !controller.signal.aborted && generation === this.pollGeneration && this.selectedId === dataset.id;
      let keepPolling = false;
      try {
        const response = await apiService.getTabularDataset(dataset.id, this.entraId, controller.signal);
        if (!current()) return;
        if (!response?.dataset || response.dataset.id !== dataset.id) throw new Error('Status response is missing the requested dataset.');
        const updated = response.dataset;
        this.datasets = this.datasets.map(item => item.id === updated.id ? updated : item);
        keepPolling = ['queued', 'processing', 'deleting'].includes(updated.status);
        if (keepPolling && updated.job?.id != null) {
          const jobResponse = await apiService.getTabularJob(updated.job.id, this.entraId, controller.signal);
          if (!current()) return;
          if (!jobResponse?.job) throw new Error('Job status response is missing job.');
          this.datasets = this.datasets.map(item => item.id === updated.id ? { ...item, job: jobResponse.job } : item);
          if (['failed', 'cancelled'].includes(jobResponse.job.status)) keepPolling = false;
        }
        this.pollError = '';
        this.pollDelay = 2000;
      } catch (error) {
        if (!current()) return;
        this.pollError = rowsErrorMessage(error);
        const status = error.response?.status;
        keepPolling = (!status || status >= 500 || status === 429) && ['queued', 'processing', 'deleting'].includes(this.selected?.status);
        this.pollDelay = Math.min(this.pollDelay * 2, 15000);
      }
      if (current() && keepPolling) this.pollTimer = setTimeout(() => this.poll(generation), this.pollDelay);
    },
  },
};
</script>

<style scoped>
.datasets-overlay { position: fixed; inset: 0; z-index: 1200; background: #0008; display: flex; justify-content: center; align-items: center; padding: 20px; }
.datasets-modal { width: min(1200px, 98vw); height: 92vh; background: white; border-radius: 12px; box-shadow: 0 16px 70px #0005; display: flex; flex-direction: column; overflow: hidden; }
.datasets-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; background: #18392b; color: white; }
.datasets-header h2 { font-size: 1.25rem; margin: 0; }
.datasets-header button { color: white; background: transparent; border: 0; }
.dataset-tabs { display: flex; border-bottom: 1px solid #ddd; padding: 0 20px; }
.dataset-tabs button { padding: 12px 20px; border: 0; background: transparent; color: #18392b; }
.dataset-tabs .active { border-bottom: 3px solid #18392b; font-weight: bold; }
.datasets-body { overflow: auto; padding: 20px; flex: 1; }
.dataset-picker { display: flex; gap: 12px; align-items: flex-end; flex-wrap: wrap; margin-bottom: 18px; }
.dataset-picker label { display: grid; gap: 6px; flex: 1; }
select, button { padding: 8px 12px; border-radius: 5px; }
select { max-width: 100%; }
button:disabled { opacity: .5; }
.status-card { padding: 16px; background: #f5f8f6; border: 1px solid #d5e4d9; border-radius: 8px; margin-bottom: 20px; }
.status-card h3 { font-size: 1.1rem; }
.badge { color: #18392b; background: #dceee0; }
.error { color: #9c2020; white-space: pre-wrap; overflow-wrap: anywhere; }
.notice { background: #fff5d5; padding: 12px; }
.dataset-actions { display: flex; flex-wrap: wrap; gap: 10px; margin: 14px 0; }
.dataset-actions button { background: white; border: 1px solid #c5d6ca; color: #294c37; }
.dataset-actions .danger-action { color: #a42a2a; border-color: #dfb4b4; }
.dataset-actions .danger-action:hover:not(:disabled) { background: #fff0ef; }
button:focus-visible { outline: 3px solid #7eac8c; outline-offset: 3px; }
</style>